import { useState, useContext, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import getSocket from "../../../services/socket.js";
import { chatRoomKey, registerOpenChat, isPageVisible } from "../../../services/chat-presence.js";
import formatRelativeTime from "../../../utils/format-relative-time.js";

import {
  MessageSquare, Send, X, CheckCircle, AlertCircle, FileText, File,
  Music, Download, Clock, CheckCheck, Check, Image as ImageIcon,
  Paperclip, Copy, Trash2, Smile, MoreVertical, Pencil, CornerUpRight,
  Forward, Maximize2, Info, ArrowDown,
} from "lucide-react";
import { AuthContext } from "../../../context/AuthContext";
import { useAlert } from "../../../context/AlertContext";
import ConfirmDialogue from "../../../components/ui/ConfirmDialogue.jsx";
import EmojiPicker from "./emoji-picker.jsx";
import MessageInfoModal from "./message-info-modal.jsx";
import Avatar from "../../../components/ui/Avatar.jsx";
import Skeleton from "../../../components/ui/Skeleton.jsx";

// Incoming-message notification sound
const NOTIFICATION_SOUND_URL =
  "https://swiftboard-bucket.s3.ap-south-1.amazonaws.com/sounds/mixkit-long-pop-2358.mp3";

// Circular Progress Component
const CircularProgress = ({ percentage, status }) => {
  const radius = 20;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  const getStatusColor = () => {
    switch (status) {
      case "error":     return "var(--color-danger)";
      case "completed": return "var(--color-success)";
      default:          return "var(--color-primary)";
    }
  };

  return (
    <div className="relative w-[50px] h-[50px]">
      <svg width="50" height="50" viewBox="0 0 50 50">
        <circle cx="25" cy="25" r={radius} fill="none" stroke="var(--color-border)" strokeWidth="4" />
        <circle
          cx="25" cy="25" r={radius} fill="none" stroke={getStatusColor()} strokeWidth="4"
          strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset}
          transform="rotate(-90 25 25)" style={{ transition: "stroke-dashoffset 0.3s ease" }}
        />
        {status === "completed" && (
          <CheckCircle x="15" y="15" size={20} color="var(--color-success)"
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
        )}
        {status === "error" && (
          <AlertCircle x="15" y="15" size={20} color="var(--color-danger)"
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
        )}
        {status === "uploading" && (
          <text x="25" y="30" textAnchor="middle" fontSize="10" fill="var(--color-text-secondary)">
            {Math.round(percentage)}%
          </text>
        )}
      </svg>
    </div>
  );
};

// Message Status Component
const MessageStatus = ({ status, timestamp, isOwn, isEdited, isSending }) => {
  const getStatusIcon = () => {
    switch (status) {
      case "SENT":      return <Check size={13} className="text-text_muted" />;
      case "DELIVERED": return <CheckCheck size={13} className="text-text_muted" />;
      case "READ":      return <CheckCheck size={13} className="text-info" />;
      default:          return <Clock size={12} className="text-text_muted" />;
    }
  };

  // Optimistic state: message is on screen but not yet confirmed by the server
  if (isSending) {
    return (
      <div className={`flex items-center gap-1 text-[10px] mt-1 ${isOwn ? "justify-end" : "justify-start"}`}>
        <Clock size={11} className="text-text_muted animate-pulse" />
        <span className="text-text_muted italic">Sending…</span>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-1 text-[10px] mt-1 ${isOwn ? "justify-end" : "justify-start"}`}>
      {isEdited && <span className="text-text_muted italic">edited</span>}
      <span className="text-text_muted">{formatRelativeTime(timestamp)}</span>
      {isOwn && getStatusIcon()}
    </div>
  );
};

// Hover Action Menu (3-dot) — menu is portaled to <body> and clamped to the
// viewport so it never overflows the screen on small / mobile devices.
const MENU_WIDTH = 168;

const MessageMenu = ({ isOwn, canCopy, onCopy, onEdit, onForward, onDelete, onInfo }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const items = [];
  if (canCopy) items.push({ label: "Copy", icon: Copy, onClick: onCopy });
  if (isOwn) items.push({ label: "Edit", icon: Pencil, onClick: onEdit });
  items.push({ label: "Forward", icon: Forward, onClick: onForward });
  items.push({ label: "Info", icon: Info, onClick: onInfo });
  if (isOwn) items.push({ label: "Delete", icon: Trash2, onClick: onDelete, danger: true });

  const computePosition = () => {
    const btn = btnRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const margin = 8;
    const menuH = items.length * 38 + 14;

    // Horizontal: align to the button, then clamp inside the viewport
    let left = isOwn ? r.right - MENU_WIDTH : r.left;
    left = Math.max(margin, Math.min(left, window.innerWidth - MENU_WIDTH - margin));

    // Vertical: prefer below the button; flip above if it would overflow
    let top = r.bottom + 6;
    if (top + menuH > window.innerHeight - margin) {
      top = Math.max(margin, r.top - menuH - 6);
    }

    setPos({ top, left });
  };

  const toggle = () => {
    if (!open) computePosition();
    setOpen((o) => !o);
  };

  useEffect(() => {
    if (!open) return;
    const onPointer = (e) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const close = () => setOpen(false);
    document.addEventListener("mousedown", onPointer);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        onClick={toggle}
        aria-label="Message actions"
        className={`w-6 h-6 flex items-center justify-center rounded-full bg-surface border border-border
          shadow-[0_2px_8px_rgba(0,0,0,0.08)] hover:bg-surface_alt transition-opacity duration-150
          ${open ? "opacity-100" : "opacity-0 group-hover:opacity-100 focus:opacity-100"}`}
      >
        <MoreVertical size={14} className="text-text_secondary" />
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          style={{ position: "fixed", top: pos.top, left: pos.left, width: MENU_WIDTH }}
          className="z-[10050] py-1.5 bg-surface border border-border rounded-xl
            shadow-[0_8px_24px_rgba(0,0,0,0.16)] slide-in overflow-hidden"
        >
          {items.map((it, i) => (
            <button
              key={i}
              onClick={() => { setOpen(false); it.onClick?.(); }}
              className={`w-full flex items-center gap-2.5 px-3.5 py-2 text-[12.5px] text-left
                transition-colors ${it.danger
                  ? "text-danger hover:bg-danger_bg"
                  : "text-text_primary hover:bg-surface_alt"}`}
            >
              <it.icon size={14} className={it.danger ? "text-danger" : "text-text_secondary"} />
              {it.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
};

// Quoted / forwarded reference shown above a bubble
const QuotedMessage = ({ reply, isOwn, onClick }) => {
  if (!reply) return null;
  const hasAttachment = reply.message_attachments?.length > 0;
  const preview = reply.is_deleted
    ? "Message deleted"
    : reply.message || (hasAttachment ? reply.message_attachments[0].file_name : "Attachment");

  return (
    <div
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      role={onClick ? "button" : undefined}
      title={onClick ? "Go to original message" : undefined}
      className={`mb-1.5 px-2.5 py-1.5 rounded-lg border-l-[3px] max-w-full overflow-hidden
        transition-colors ${onClick ? "cursor-pointer" : ""} ${
        isOwn
          ? "bg-white/[0.12] border-white/60 hover:bg-white/[0.2]"
          : "bg-surface_alt border-primary hover:bg-primary_tint"
      }`}
    >
      <p className={`text-[10.5px] font-semibold truncate ${isOwn ? "text-white/90" : "text-primary"}`}>
        {reply.sender?.first_name} {reply.sender?.last_name}
      </p>
      <p className={`text-[11px] truncate flex items-center gap-1 ${isOwn ? "text-white/70" : "text-text_muted"}`}>
        {hasAttachment && <Paperclip size={10} className="flex-shrink-0" />}
        <span className="truncate">{preview}</span>
      </p>
    </div>
  );
};

// Splits message text on URLs and renders them as clickable links
const URL_REGEX = /(https?:\/\/[^\s]+)/g;

const linkifyText = (text, isOwn) => {
  if (!text) return text;
  const parts = text.split(URL_REGEX);
  if (parts.length === 1) return text;

  return parts.map((part, i) => {
    const isUrl = i % 2 === 1;
    if (!isUrl) return part;

    const trimmed = part.replace(/[.,;:!?)]+$/, "");
    const trailing = part.slice(trimmed.length);
    return (
      <span key={i}>
        <a
          href={trimmed}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className={`underline break-all ${isOwn ? "text-white" : "text-info"}`}
        >
          {trimmed}
        </a>
        {trailing}
      </span>
    );
  });
};

// Message Bubble Component
const MessageBubble = ({
  message, isOwn, withChat, isHighlighted, readOnly = false,
  onCopy, onEdit, onForward, onDelete, onQuoteClick, onInfo,
}) => {
  const canCopy = !!message.message;

  return (
    <div
      id={`chat-msg-${message.id || message.message_id}`}
      style={{ scrollMarginTop: "16px", scrollMarginBottom: "16px" }}
      className={`flex ${isOwn ? "justify-end" : "justify-start"} group fade-in`}
    >
      <div className={`flex max-w-[78%] ${isOwn ? "flex-row-reverse" : "flex-row"} items-end gap-2`}>
        {/* Avatar for other users */}
        {!isOwn && (
          <div className="flex-shrink-0">
            <Avatar user={message.sender || withChat} size={30} />
          </div>
        )}

        <div className={`flex flex-col min-w-0 ${isOwn ? "items-end" : "items-start"}`}>
          {/* Sender Name */}
          {!isOwn && (
            <span className="text-[11px] font-medium text-text_secondary mb-0.5 ml-1">
              {withChat?.first_name} {withChat?.last_name}
            </span>
          )}

          {/* Bubble + absolute action menu (absolute => never shifts layout) */}
          <div
            className={`relative max-w-full rounded-2xl transition-opacity duration-200
              ${isOwn ? "rounded-br-md" : "rounded-bl-md"}
              ${message.sending ? "opacity-70" : "opacity-100"}
              ${isHighlighted ? "chat-flash" : ""}`}
          >
            {!message.is_deleted && !readOnly && (
              <div className={`absolute top-0 z-20 ${isOwn ? "-left-7" : "-right-7"}`}>
                <MessageMenu
                  isOwn={isOwn}
                  canCopy={canCopy}
                  onCopy={onCopy}
                  onEdit={onEdit}
                  onForward={onForward}
                  onDelete={onDelete}
                  onInfo={onInfo}
                />
              </div>
            )}

          {/* Deleted Message */}
          {message.is_deleted ? (
            <div className="px-4 py-2.5 bg-surface rounded-2xl text-text_muted italic
              text-[13px] border border-border">
              This message was deleted
            </div>
          ) : (
            <>
              {/* Forwarded label */}
              {message.is_forwarded && (
                <div className={`flex items-center gap-1 mb-1 text-[10.5px] italic text-text_muted
                  ${isOwn ? "justify-end" : "justify-start"}`}>
                  <CornerUpRight size={11} />
                  <span>Forwarded</span>
                </div>
              )}

              {/* Attachments */}
              {message.message_attachments?.length > 0 && (
                <div className={`space-y-2 mb-1.5 ${isOwn ? "items-end" : "items-start"}`}>
                  {message.message_attachments.map((file, i) => {
                    const isImage = file.file_type?.startsWith("image/");
                    const isVideo = file.file_type?.startsWith("video/");
                    const isAudio = file.file_type?.startsWith("audio/");
                    const isPDF = file.file_type === "application/pdf";

                    return (
                      <div
                        key={i}
                        className={`overflow-hidden ${
                          isOwn ? "bg-primary" : "bg-surface border border-border"
                        } rounded-2xl ${isOwn ? "rounded-br-md" : "rounded-bl-md"}
                        transition-all duration-200`}
                      >
                        {/* Image */}
                        {isImage && (
                          <div
                            className="relative group/img cursor-pointer"
                            onClick={() => window.open(file.file_url, "_blank")}
                          >
                            <img
                              src={file.file_url}
                              alt={file.file_name}
                              className="max-w-full max-h-80 object-cover"
                            />
                            <div className="absolute inset-0 bg-black/40 opacity-0
                              group-hover/img:opacity-100 transition-opacity flex items-center
                              justify-center">
                              <span className="text-white text-[13px] font-medium">
                                Click to view
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Video */}
                        {isVideo && (
                          <video src={file.file_url} controls className="max-w-full max-h-80" />
                        )}

                        {/* Audio */}
                        {isAudio && (
                          <div className="p-3.5 flex items-center gap-3 min-w-[240px]">
                            <div className={`w-9 h-9 rounded-full flex items-center justify-center
                              flex-shrink-0 ${isOwn ? "bg-white/15" : "bg-primary_tint"}`}>
                              <Music size={18} className={isOwn ? "text-white" : "text-primary"} />
                            </div>
                            <audio src={file.file_url} controls className="flex-1 h-9" />
                          </div>
                        )}

                        {/* File (PDF, etc.) */}
                        {!isImage && !isVideo && !isAudio && (
                          <div
                            className={`p-3 flex items-center gap-3 cursor-pointer transition-all
                              min-w-[230px] ${isOwn ? "bg-white/[0.08]" : "bg-surface_alt"}`}
                            onClick={() => window.open(file.file_url, "_blank")}
                          >
                            {isPDF ? (
                              <div className={`w-9 h-9 rounded-[9px] flex items-center justify-center
                                flex-shrink-0 ${isOwn ? "bg-white/15" : "bg-danger_bg"}`}>
                                <FileText size={18} className={isOwn ? "text-white" : "text-danger"} />
                              </div>
                            ) : (
                              <div className={`w-9 h-9 rounded-[9px] flex items-center justify-center
                                flex-shrink-0 ${isOwn ? "bg-white/15" : "bg-surface"}`}>
                                <File size={18} className={isOwn ? "text-white" : "text-text_secondary"} />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className={`text-[12.5px] font-medium truncate ${
                                isOwn ? "text-white" : "text-text_primary"}`}>
                                {file.file_name}
                              </p>
                              <p className={`text-[10.5px] ${
                                isOwn ? "text-white/70" : "text-text_muted"}`}>
                                {file.file_size}
                              </p>
                            </div>
                            <Download size={17} className={isOwn ? "text-white/70" : "text-text_muted"} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Text Message (+ quoted/forwarded reference inside the same bubble) */}
              {(message.message || message.reply_to) && (
                <div
                  className={`px-3.5 py-2.5 text-[12.5px] leading-relaxed ${
                    isOwn
                      ? "bg-primary text-white rounded-2xl rounded-br-md"
                      : "bg-surface border border-border text-text_primary rounded-2xl rounded-bl-md"
                  }`}
                >
                  {message.reply_to && (
                    <QuotedMessage
                      reply={message.reply_to}
                      isOwn={isOwn}
                      onClick={onQuoteClick}
                    />
                  )}
                  {message.message && (
                    <p className="whitespace-pre-wrap break-words">
                      {linkifyText(message.message, isOwn)}
                    </p>
                  )}
                </div>
              )}
            </>
          )}
          </div>

          {/* Message Status */}
          <MessageStatus
            status={message.status}
            timestamp={message.updated_date || message.timestamp}
            isOwn={isOwn}
            isEdited={message.is_edited}
            isSending={message.sending}
          />
        </div>
      </div>
    </div>
  );
};

// Loading Skeleton
const ChatLoadingSkeleton = () => (
  <div className="space-y-4 p-4">
    {[1, 2, 3, 4].map((i) => (
      <div key={i} className={`flex ${i % 2 === 0 ? "justify-start" : "justify-end"}`}>
        <Skeleton
          className="border border-border"
          rounded="rounded-2xl"
          style={{ width: i % 2 === 0 ? "60%" : "50%", height: "52px" }}
        />
      </div>
    ))}
  </div>
);

export default function GlobalMessageTab({ project, api, withChat, chatType = "CLIENT_ADMIN", standalone = false, readOnly = false }) {
  const { user } = useContext(AuthContext);
  const socket = getSocket();
  const typingTimerRef = useRef(null);
  const viewMessageLimit = 20;
  const currentLoggedInUserId = user?.id;
  const { showAlert } = useAlert();

  const [messageInput, setMessageInput] = useState("");
  const [editingMessage, setEditingMessage] = useState(null);   // message being edited
  const [forwardingMessage, setForwardingMessage] = useState(null); // message being forwarded/quoted
  const [confirmData, setConfirmData] = useState(null);         // delete confirmation
  const [infoMessage, setInfoMessage] = useState(null);         // message info modal
  const [showScrollDown, setShowScrollDown] = useState(false);  // jump-to-latest button
  const [socketReady, setSocketReady] = useState(false);        // tracks live socket connection
  const [pageVisible, setPageVisible] = useState(isPageVisible); // tab in foreground (gates read receipts)
  const [highlightedId, setHighlightedId] = useState(null);     // message flashed after a jump
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const highlightTimerRef = useRef(null);
  const emojiWrapRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [istyping, setIstyping] = useState(false);

  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingPrev, setLoadingPrev] = useState(false);

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [fileUploadProgress, setFileUploadProgress] = useState({});

  const chatContainerRef = useRef();
  const messagesEndRef = useRef();
  const fileInputRef = useRef();
  const textareaRef = useRef();
  const optimisticIdRef = useRef(0);
  const notificationAudioRef = useRef(null);
  const restoreScrollRef = useRef(null);

  const scrollToBottom = (behavior = "smooth") => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  const handleMessagesScroll = (e) => {
    const el = e.target;
    if (el.scrollTop === 0 && hasMore && !loadingPrev) {
      loadPreviousMessages();
    }
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setShowScrollDown(distanceFromBottom > 250);
  };

  useEffect(() => {
    if (!project?.id) return;

    const loadMessages = async () => {
      setLoading(true);
      try {
        const res = await api.getMessages({
          project_fid: project.id,
          skip: 0,
          limit: viewMessageLimit,
        });

        if (res.data?.status && res.data.data) {
          const formatted = res.data.data.messages.map((msg) => ({
            ...msg,
            senderId: String(msg.sender_fid),
            message_id: msg.id,
            timestamp: new Date(msg.created_date),
            updated_date: new Date(msg.updated_date),
          }));

          setMessages(formatted);
          setSkip(viewMessageLimit);
          setHasMore(res.data.data.hasMore);

          setTimeout(() => scrollToBottom("auto"), 100);
        }
      } catch (err) {
        console.error("Load Messages Error:", err);
      } finally {
        setLoading(false);
      }
    };

    loadMessages();

    return () => {
      setMessages([]);
      setSkip(0);
      setHasMore(false);
    };
  }, [project?.id, api]);

  const loadPreviousMessages = async () => {
    if (!hasMore || loadingPrev) return;

    setLoadingPrev(true);
    const container = chatContainerRef.current;

    // Anchor on the current top message so its on-screen position is preserved
    // after older messages are prepended (no jump / focus disturbance).
    const topMsg = messages[0];
    const anchorId = topMsg ? (topMsg.id || topMsg.message_id) : null;
    const anchorEl = anchorId != null ? document.getElementById(`chat-msg-${anchorId}`) : null;

    try {
      const res = await api.getMessages({
        project_fid: project.id,
        skip,
        limit: viewMessageLimit,
      });

      if (res.data?.status && res.data.data) {
        const formatted = res.data.data.messages.map((msg) => ({
          ...msg,
          senderId: String(msg.sender_fid),
          message_id: msg.id,
          timestamp: new Date(msg.created_date),
          updated_date: new Date(msg.updated_date),
        }));

        // Recorded just before the prepend; restored synchronously in useLayoutEffect
        restoreScrollRef.current = {
          anchorId,
          prevOffsetTop: anchorEl ? anchorEl.offsetTop : 0,
          prevScrollTop: container ? container.scrollTop : 0,
        };

        setMessages((prev) => [...formatted, ...prev]);
        setSkip((prev) => prev + viewMessageLimit);
        setHasMore(res.data.data.hasMore);
      }
    } catch (err) {
      console.error("Previous Messages Error:", err);
    } finally {
      setLoadingPrev(false);
    }
  };

  // Restore scroll position after older messages are prepended (runs before paint)
  useLayoutEffect(() => {
    const info = restoreScrollRef.current;
    if (!info) return;
    restoreScrollRef.current = null;

    const container = chatContainerRef.current;
    if (!container) return;

    const anchorEl =
      info.anchorId != null ? document.getElementById(`chat-msg-${info.anchorId}`) : null;
    if (anchorEl) {
      // Keep the anchor message exactly where it was relative to the viewport
      container.scrollTop = info.prevScrollTop + (anchorEl.offsetTop - info.prevOffsetTop);
    }
  }, [messages]);

  const uploadFilesAndGetAttachments = async (files) => {
    const attachments = [];
    setUploadingFiles(true);

    const initialProgress = {};
    files.forEach((file, index) => {
      initialProgress[index] = { progress: 0, status: "uploading", fileUrl: null, error: null };
    });
    setFileUploadProgress(initialProgress);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      try {
        let progressInterval;
        if (!api.uploadToS3.toString().includes("onProgress")) {
          progressInterval = setInterval(() => {
            setFileUploadProgress((prev) => ({
              ...prev,
              [i]: { ...prev[i], progress: Math.min(prev[i]?.progress + 10, 90) },
            }));
          }, 200);
        }

        const fileUrl = await api.uploadToS3(file, project.title, {
          onProgress: (progress) => {
            setFileUploadProgress((prev) => ({
              ...prev,
              [i]: { ...prev[i], progress: progress },
            }));
          },
        });

        if (progressInterval) clearInterval(progressInterval);

        attachments.push({
          file_url: fileUrl,
          file_name: file.name,
          file_size: `${(file.size / (1024 * 1024)).toFixed(2)} MB`,
          file_type: file.type,
          tempId: `temp-${Date.now()}-${i}`,
        });

        setFileUploadProgress((prev) => ({
          ...prev,
          [i]: { ...prev[i], progress: 100, status: "completed", fileUrl: fileUrl },
        }));
      } catch (error) {
        console.error(`Error uploading file ${file.name}:`, error);
        setFileUploadProgress((prev) => ({
          ...prev,
          [i]: { ...prev[i], status: "error", error: error.message || "Upload failed" },
        }));
      }
    }

    setUploadingFiles(false);
    return attachments;
  };

  const handleRemoveFile = async (index) => {
    const progressData = fileUploadProgress[index];

    if (progressData?.fileUrl && progressData.status === "completed") {
      try {
        await api.deleteUploadedFile?.(progressData.fileUrl);
      } catch (error) {
        console.error("Error deleting file from server:", error);
      }
    }

    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setFileUploadProgress((prev) => {
      const newProgress = { ...prev };
      delete newProgress[index];
      return newProgress;
    });
  };

  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const files = [];
    for (const item of items) {
      if (item.kind === "file") {
        const file = item.getAsFile();
        if (file) files.push(file);
      }
    }

    if (files.length > 0) {
      e.preventDefault();
      const MAX_FILE_SIZE = 20 * 1024 * 1024;
      const validFiles = files.filter((f) => f.size <= MAX_FILE_SIZE);
      if (validFiles.length < files.length) {
        showAlert({ type: "warning", message: "Some files exceed 20MB and were not added!" });
      }
      setSelectedFiles((prev) => [...prev, ...validFiles]);
    }
  };

  // Keep a reactive flag for the socket's connection state so effects can
  // re-run once it connects (important on a freshly opened tab / deep link).
  useEffect(() => {
    const onConnect = () => setSocketReady(true);
    const onDisconnect = () => setSocketReady(false);
    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    setSocketReady(socket.connected);
    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
    };
  }, [socket]);

  // Messages only count as read while this tab is actually in front of the
  // user. A chat left open in a background tab must not turn ticks blue —
  // it re-runs the read check as soon as the tab becomes visible again.
  useEffect(() => {
    const onVisibility = () => setPageVisible(isPageVisible());
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  useEffect(() => {
    if (readOnly) return;
    if (!pageVisible) return;
    if (!messages.length || !project?.id || !currentLoggedInUserId || !socketReady) return;

    const unread = messages.filter(
      (msg) =>
        msg.status !== "READ" &&
        msg.sender_fid !== currentLoggedInUserId &&
        !msg.is_deleted
    );

    if (unread.length > 0) {
      socket.emit("message-read", {
        project_fid: project.id,
        message_ids: unread.map((m) => m.id),
        chat_type: chatType,
      });
    }
  }, [messages, project?.id, currentLoggedInUserId, socket, socketReady, chatType, pageVisible, readOnly]);

  useEffect(() => {
    if (readOnly) return;
    if (!project?.id || !socket) return;

    if (!socket.connected) socket.connect();

    // Lets the server's receiver-check know this chat is open in this tab
    const unregisterOpenChat = registerOpenChat(chatRoomKey(chatType, project.id));

    const joinRoom = () => socket.emit("join-room", { project_fid: project.id, chat_type: chatType });
    joinRoom();

    // After a reconnect the server-side socket is brand new and in no rooms,
    // so this open chat would silently stop receiving messages. Re-join, and
    // pull the latest page so anything sent while disconnected shows up and
    // tick states catch up.
    let hasConnectedBefore = socket.connected;
    const syncLatestMessages = async () => {
      try {
        const res = await api.getMessages({ project_fid: project.id, skip: 0, limit: viewMessageLimit });
        const latest = res.data?.status ? res.data.data?.messages || [] : [];
        if (!latest.length) return;
        const byId = new Map(latest.map((m) => [m.id, m]));
        setMessages((prev) => {
          const known = new Set(prev.map((m) => m.id));
          const updated = prev.map((m) => {
            const fresh = byId.get(m.id);
            return fresh
              ? { ...m, status: fresh.status, delivered_date: fresh.delivered_date, readed_date: fresh.readed_date }
              : m;
          });
          const missed = latest
            .filter((m) => !known.has(m.id))
            .map((m) => ({
              ...m,
              senderId: String(m.sender_fid),
              message_id: m.id,
              timestamp: new Date(m.created_date),
              updated_date: new Date(m.updated_date),
            }))
            .sort((a, b) => a.timestamp - b.timestamp);
          return missed.length ? [...updated, ...missed] : updated;
        });
      } catch (err) {
        console.error("Chat resync error:", err);
      }
    };
    const handleReconnect = () => {
      joinRoom();
      if (hasConnectedBefore) syncLatestMessages();
      hasConnectedBefore = true;
    };

    const handleNewMessage = (msg) => {
      const normalized = {
        ...msg,
        message_id: msg.id,
        senderId: String(msg.sender_fid),
        sending: false,
        timestamp: new Date(msg.created_date),
        updated_date: new Date(msg.updated_date),
      };

      setMessages((prev) => {
        // Reconcile our own optimistic message with the server's confirmed copy
        if (msg.temp_id) {
          const idx = prev.findIndex((m) => m.temp_id === msg.temp_id);
          if (idx !== -1) {
            const next = [...prev];
            next[idx] = normalized;
            return next;
          }
        }

        const exists = prev.some((m) => m.message_id === msg.id || m.id === msg.id);
        if (exists) return prev;

        return [...prev, normalized];
      });

      setTimeout(() => scrollToBottom(), 100);

      if (msg.sender_fid !== currentLoggedInUserId) {
        // Play the incoming-message sound (ignore autoplay rejections)
        const audio = notificationAudioRef.current;
        if (audio) {
          try {
            audio.currentTime = 0;
            audio.play().catch(() => {});
          } catch {
            /* no-op */
          }
        }

        // Only acknowledge delivery when the message is actually in front of
        // the user. In a background tab it stays SENT (single tick) and the
        // server's email path covers it; returning to the tab marks it READ.
        if (socket.connected && isPageVisible()) {
          socket.emit("message-delivered", {
            project_fid: project.id,
            message_id: msg.id,
            chat_type: chatType,
            visible: true,
          });
        }
      }
    };

    const handleDeleted = ({ message_ids }) => {
      setMessages((prev) =>
        prev.map((msg) =>
          message_ids.includes(String(msg.message_id)) || message_ids.includes(String(msg.id))
            ? { ...msg, is_deleted: true }
            : msg
        )
      );
    };

    const handleEdited = (updated) => {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === updated.id || msg.message_id === updated.id
            ? {
                ...msg,
                ...updated,
                message_id: updated.id,
                senderId: String(updated.sender_fid),
                timestamp: msg.timestamp,
                updated_date: new Date(updated.updated_date),
              }
            : msg
        )
      );
    };

    const handleTyping = (payload) => {
      if (payload.typer_id !== user?.id) {
        setIstyping(true);
        if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
        typingTimerRef.current = setTimeout(() => setIstyping(false), 3000);
      }
    };

    const handleRead = ({ message_ids, readed_date }) => {
      const ids = message_ids.map(String);
      setMessages((prev) =>
        prev.map((msg) =>
          ids.includes(String(msg.id)) || ids.includes(String(msg.message_id))
            ? { ...msg, status: "READ", readed_date: readed_date || msg.readed_date }
            : msg
        )
      );
    };

    const handleMessageStatus = ({ message_id, status, delivered_date }) => {
      setMessages((prev) =>
        prev.map((msg) =>
          // Never downgrade: a late DELIVERED must not turn blue ticks grey
          (msg.id === message_id || msg.message_id === message_id) && msg.status !== "READ"
            ? { ...msg, status, delivered_date: delivered_date || msg.delivered_date }
            : msg
        )
      );
    };

    socket.on("new-message", handleNewMessage);
    socket.on("message-deleted", handleDeleted);
    socket.on("message-edited", handleEdited);
    socket.on("message-status-read", handleRead);
    socket.on("message-status", handleMessageStatus);
    socket.on("typing", handleTyping);
    socket.on("connect", handleReconnect);

    return () => {
      unregisterOpenChat();
      socket.off("connect", handleReconnect);
      socket.off("new-message", handleNewMessage);
      socket.off("message-deleted", handleDeleted);
      socket.off("message-edited", handleEdited);
      socket.off("message-status-read", handleRead);
      socket.off("message-status", handleMessageStatus);
      socket.off("typing", handleTyping);

      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);

      socket.emit("leave-room", { project_fid: project.id, chat_type: chatType });
    };
  }, [project?.id, user?.id, socket, currentLoggedInUserId, chatType, readOnly]);

  const resetComposer = () => {
    setMessageInput("");
    setSelectedFiles([]);
    setFileUploadProgress({});
    setEditingMessage(null);
    setForwardingMessage(null);
    setShowEmojiPicker(false);
    if (textareaRef.current) textareaRef.current.style.height = "auto";
  };

  const handleSendMessage = async () => {
    // --- Edit mode: update existing message text ---
    if (editingMessage) {
      const trimmed = messageInput.trim();
      if (!trimmed) return;
      if (trimmed !== (editingMessage.message || "")) {
        socket.emit("edit-message", {
          project_fid: project.id,
          message_id: editingMessage.id || editingMessage.message_id,
          message: trimmed,
          chat_type: chatType,
        });
      }
      resetComposer();
      return;
    }

    if ((!messageInput.trim() && selectedFiles.length === 0) || uploadingFiles) return;

    let attachments = [];

    if (selectedFiles.length) {
      attachments = await uploadFilesAndGetAttachments(selectedFiles);
    }

    const hasErrors = Object.values(fileUploadProgress).some((p) => p.status === "error");

    if (hasErrors) {
      showAlert({ type: "error", message: "Some files failed to upload. Please remove them and try again." });
      return;
    }

    const temp_id = `temp-${Date.now()}-${optimisticIdRef.current++}`;
    const forwardRef = forwardingMessage;

    const payload = {
      project_fid: project.id,
      message: messageInput || "",
      message_type: attachments.length ? "FILE" : "TEXT",
      message_attachments: attachments,
      chat_type: chatType,
      reply_to_fid: forwardRef ? (forwardRef.id || forwardRef.message_id) : null,
      is_forwarded: !!forwardRef,
      temp_id,
    };

    // Optimistic UI: render instantly as "Sending…" before the server confirms
    const now = new Date();
    const optimisticMessage = {
      id: temp_id,
      message_id: temp_id,
      temp_id,
      sender_fid: currentLoggedInUserId,
      senderId: String(currentLoggedInUserId),
      message: payload.message,
      message_type: payload.message_type,
      message_attachments: attachments,
      status: "SENDING",
      sending: true,
      is_forwarded: !!forwardRef,
      reply_to_fid: payload.reply_to_fid,
      reply_to: forwardRef
        ? {
            id: forwardRef.id || forwardRef.message_id,
            message: forwardRef.message,
            message_type: forwardRef.message_type,
            is_deleted: forwardRef.is_deleted,
            sender: forwardRef.sender,
            message_attachments: forwardRef.message_attachments || [],
          }
        : null,
      sender: {
        id: currentLoggedInUserId,
        first_name: user?.first_name,
        last_name: user?.last_name,
        profile_picture_url: user?.profile_picture_url,
      },
      created_date: now,
      updated_date: now,
      timestamp: now,
    };

    setMessages((prev) => [...prev, optimisticMessage]);
    setTimeout(() => scrollToBottom(), 50);

    socket.emit("send-message", payload);

    resetComposer();
  };

  // ─── Message action handlers ──────────────────────────────────────
  const handleCopyMessage = async (message) => {
    if (!message.message) return;
    try {
      await navigator.clipboard.writeText(message.message);
      showAlert({ type: "success", message: "Message copied to clipboard" });
    } catch {
      showAlert({ type: "error", message: "Unable to copy message" });
    }
  };

  const handleStartEdit = (message) => {
    setForwardingMessage(null);
    setEditingMessage(message);
    setMessageInput(message.message || "");
    setTimeout(() => {
      const el = textareaRef.current;
      if (el) {
        el.focus();
        el.style.height = "auto";
        el.style.height = Math.min(el.scrollHeight, 120) + "px";
      }
    }, 50);
  };

  const handleStartForward = (message) => {
    setEditingMessage(null);
    setForwardingMessage(message);
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  const handleRequestDelete = (message) => {
    setConfirmData({
      _message: message,
      title: "Delete message?",
      message: "This message will be removed for everyone in this conversation. This action cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
      variant: "danger",
      icon: <Trash2 size={20} />,
    });
  };

  const handleConfirmClose = (confirmed) => {
    const message = confirmData?._message;
    setConfirmData(null);
    if (confirmed && message) {
      socket.emit("deletion-message", {
        project_fid: project.id,
        message_ids: [message.id || message.message_id],
        chat_type: chatType,
      });
    }
  };

  // Smooth-scroll to the original message a quote/forward points to, then flash it
  const scrollToMessage = (messageId) => {
    if (!messageId) return;
    const el = document.getElementById(`chat-msg-${messageId}`);
    if (!el) {
      showAlert({
        type: "info",
        message: "Original message isn't loaded yet. Scroll up to load older messages.",
      });
      return;
    }
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightedId(messageId);
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    highlightTimerRef.current = setTimeout(() => setHighlightedId(null), 1800);
  };

  useEffect(() => () => {
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
  }, []);

  // Prepare the incoming-message sound and unlock autoplay on first user gesture
  useEffect(() => {
    const audio = new Audio(NOTIFICATION_SOUND_URL);
    audio.preload = "auto";
    audio.volume = 0.6;
    notificationAudioRef.current = audio;

    const unlock = () => {
      // Browser autoplay policy: prime the element during a user gesture
      audio.muted = true;
      audio
        .play()
        .then(() => {
          audio.pause();
          audio.currentTime = 0;
          audio.muted = false;
        })
        .catch(() => {
          audio.muted = false;
        });
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };

    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);

    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);

  // Insert an emoji at the textarea caret (keeps focus + auto-grow)
  const insertEmoji = (emoji) => {
    const el = textareaRef.current;
    if (!el) {
      setMessageInput((prev) => prev + emoji);
      return;
    }
    const start = el.selectionStart ?? messageInput.length;
    const end = el.selectionEnd ?? messageInput.length;
    const next = messageInput.slice(0, start) + emoji + messageInput.slice(end);
    setMessageInput(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + emoji.length;
      el.setSelectionRange(pos, pos);
      el.style.height = "auto";
      el.style.height = Math.min(el.scrollHeight, 120) + "px";
    });
  };

  // Close the emoji picker on outside click
  useEffect(() => {
    if (!showEmojiPicker) return;
    const handler = (e) => {
      if (emojiWrapRef.current && !emojiWrapRef.current.contains(e.target)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showEmojiPicker]);

  const handleTypingEmit = () => {
    if (socket.connected && messageInput.trim()) {
      socket.emit("typing", { project_fid: project.id, typer_id: user?.id, chat_type: chatType });
    }
  };

  const isSendDisabled =
    (!messageInput.trim() && selectedFiles.length === 0 && !forwardingMessage) || uploadingFiles;

  const openFullScreen = () => {
    if (!project?.id) return;
    const slug = chatType === "ADMIN_EMPLOYEE" ? "admin-employee" : "client-admin";
    const url = `/swiftboard/live-projects/conversation/${slug}?project_id=${project.id}`;
    window.open(url, "_blank", "noopener");
  };

  return (
    <div className="relative flex flex-col h-full bg-surface_alt">
      {/* Expand to full-screen chat (its own tab) */}
      {!standalone && !readOnly && project?.id && (
        <button
          onClick={openFullScreen}
          title="Open chat in full screen"
          aria-label="Open chat in full screen"
          className="absolute top-3 right-3 z-30 w-8 h-8 flex items-center justify-center
            rounded-full bg-surface border border-border text-text_secondary
            shadow-[0_2px_8px_rgba(0,0,0,0.08)] hover:bg-primary hover:text-white
            hover:border-primary transition-colors"
        >
          <Maximize2 size={15} />
        </button>
      )}

      {/* Premium "jump-to-message" double-blink glow (follows the bubble's own shape) */}
      <style>{`
        @keyframes chatMsgFlash {
          0%, 100% { box-shadow: 0 0 0 0 transparent; }
          10%, 50% {
            box-shadow: 0 0 0 2.5px var(--color-primary),
                        0 0 18px 3px color-mix(in srgb, var(--color-primary) 55%, transparent);
          }
          30%, 70% { box-shadow: 0 0 0 0 transparent; }
        }
        .chat-flash {
          animation: chatMsgFlash 1.5s cubic-bezier(0.4, 0, 0.2, 1) both;
        }
      `}</style>

      {/* Messages Container (relative wrapper anchors the jump-to-latest button) */}
      <div className="relative flex-1 min-h-0">
      <div
        ref={chatContainerRef}
        className="absolute inset-0 overflow-y-auto px-4 py-5 space-y-3.5 custom-scrollbar"
        onScroll={handleMessagesScroll}
      >
        {loading ? (
          <ChatLoadingSkeleton />
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center fade-in">
            <div className="w-20 h-20 rounded-full bg-primary_tint flex items-center
              justify-center mb-5">
              <MessageSquare size={34} className="text-primary" />
            </div>
            <h3 className="text-base font-medium text-text_primary mb-1.5">
              No messages yet
            </h3>
            <p className="text-[13px] text-text_muted max-w-xs">
              Start a conversation with {withChat?.first_name || "your team member"}
            </p>
          </div>
        ) : (
          <>
            {hasMore && (
              <div className="flex justify-center mb-2 fade-in">
                <button
                  className="px-4 py-2 text-[12px] font-medium text-primary bg-surface
                    rounded-full border border-border hover:border-primary
                    transition-all duration-200 disabled:opacity-50"
                  onClick={loadPreviousMessages}
                  disabled={loadingPrev}
                >
                  {loadingPrev ? (
                    <div className="flex items-center gap-2">
                      <div className="w-3.5 h-3.5 border-2 border-primary border-t-transparent
                        rounded-full animate-spin" />
                      <span>Loading</span>
                    </div>
                  ) : (
                    "Load previous messages"
                  )}
                </button>
              </div>
            )}

            {messages.map((msg) => {
              const isOwn = msg.sender_fid === currentLoggedInUserId;
              return (
                <MessageBubble
                  key={msg.message_id || msg.id}
                  message={msg}
                  isOwn={isOwn}
                  withChat={withChat}
                  readOnly={readOnly}
                  isHighlighted={highlightedId === (msg.id || msg.message_id)}
                  onCopy={() => handleCopyMessage(msg)}
                  onEdit={() => handleStartEdit(msg)}
                  onForward={() => handleStartForward(msg)}
                  onDelete={() => handleRequestDelete(msg)}
                  onQuoteClick={() => scrollToMessage(msg.reply_to?.id)}
                  onInfo={() => setInfoMessage(msg)}
                />
              );
            })}

            {/* Typing Indicator */}
            {istyping && (
              <div className="flex items-end gap-2 fade-in">
                <div className="flex-shrink-0">
                  <Avatar user={withChat} size={30} />
                </div>
                <div className="bg-surface border border-border px-4 py-3 rounded-2xl
                  rounded-bl-md">
                  <div className="flex gap-1.5">
                    <div className="w-1.5 h-1.5 bg-text_muted rounded-full animate-bounce [animation-delay:-0.3s]" />
                    <div className="w-1.5 h-1.5 bg-text_muted rounded-full animate-bounce [animation-delay:-0.15s]" />
                    <div className="w-1.5 h-1.5 bg-text_muted rounded-full animate-bounce" />
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </>
        )}
      </div>

        {/* Jump to latest message */}
        {showScrollDown && (
          <button
            onClick={() => { scrollToBottom("smooth"); setShowScrollDown(false); }}
            aria-label="Scroll to latest message"
            title="Scroll to latest"
            className="absolute bottom-4 right-4 z-20 w-10 h-10 flex items-center justify-center
              rounded-full bg-surface border border-border text-text_secondary
              shadow-[0_4px_14px_rgba(0,0,0,0.14)] hover:bg-primary hover:text-white
              hover:border-primary transition-all duration-200 fade-in"
          >
            <ArrowDown size={18} />
          </button>
        )}
      </div>

      {/* File Preview Area */}
      {selectedFiles.length > 0 && (
        <div className="bg-surface border-t border-border p-4 space-y-2.5 max-h-48
          overflow-y-auto custom-scrollbar slide-in">
          <div className="flex items-center justify-between">
            <h4 className="text-[13px] font-medium text-text_secondary">
              Attachments ({selectedFiles.length})
            </h4>
            {uploadingFiles && (
              <span className="text-[11px] text-primary animate-pulse">Uploading...</span>
            )}
          </div>

          {selectedFiles.map((file, index) => {
            const isImage = file.type.startsWith("image/");
            const progress = fileUploadProgress[index] || { progress: 0, status: "uploading" };

            return (
              <div
                key={index}
                className="flex items-center gap-3 bg-surface_alt rounded-xl p-2.5
                  border border-border hover:border-primary transition-all duration-200
                  group fade-in"
              >
                <div className="relative w-12 h-12 flex-shrink-0">
                  {isImage && progress.status === "completed" ? (
                    <img
                      src={URL.createObjectURL(file)}
                      alt={file.name}
                      className="w-full h-full object-cover rounded-lg"
                    />
                  ) : (
                    <div className="w-full h-full bg-surface border border-border rounded-lg
                      flex items-center justify-center">
                      <ImageIcon size={22} className="text-text_muted" />
                    </div>
                  )}

                  {(progress.status === "uploading" || progress.status === "error") && (
                    <div className="absolute inset-0 bg-surface/80 rounded-lg flex items-center
                      justify-center">
                      <CircularProgress percentage={progress.progress} status={progress.status} />
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium text-text_primary truncate">
                    {file.name}
                  </p>
                  <p className="text-[11px] text-text_muted">
                    {(file.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                  {progress.status === "error" && (
                    <p className="text-[11px] text-danger">
                      {progress.error || "Upload failed"}
                    </p>
                  )}
                </div>

                <button
                  onClick={() => handleRemoveFile(index)}
                  className="p-2 hover:bg-danger_bg rounded-lg transition-colors
                    opacity-0 group-hover:opacity-100"
                >
                  <X size={15} className="text-danger" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Read-only footer for archived / history projects */}
      {readOnly && (
        <div className="bg-surface border-t border-border px-4 py-3 flex items-center justify-center gap-2">
          <MessageSquare size={14} className="text-text_muted flex-shrink-0" />
          <p className="text-[12px] text-text_muted text-center">
            This conversation is part of the project history and is available for viewing only.
          </p>
        </div>
      )}

      {/* Message Input */}
      {!readOnly && (
      <div className="bg-surface border-t border-border p-3.5">
        {/* Edit / Forward context bar */}
        {(editingMessage || forwardingMessage) && (
          <div className="flex items-center gap-2.5 mb-2.5 px-3 py-2 bg-surface_alt border border-border
            rounded-xl slide-in">
            <div className={`w-1 self-stretch rounded-full ${editingMessage ? "bg-warning" : "bg-primary"}`} />
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {editingMessage ? (
                <Pencil size={14} className="text-warning" />
              ) : (
                <Forward size={14} className="text-primary" />
              )}
              <span className="text-[11.5px] font-semibold text-text_secondary">
                {editingMessage ? "Editing message" : "Forwarding message"}
              </span>
            </div>
            <p className="flex-1 min-w-0 truncate text-[12px] text-text_muted">
              {(editingMessage || forwardingMessage).message ||
                (editingMessage || forwardingMessage).message_attachments?.[0]?.file_name ||
                "Attachment"}
            </p>
            <button
              onClick={() => {
                if (editingMessage) setMessageInput("");
                setEditingMessage(null);
                setForwardingMessage(null);
                if (textareaRef.current) textareaRef.current.style.height = "auto";
              }}
              className="p-1 rounded-lg hover:bg-surface text-text_muted hover:text-danger
                transition-colors flex-shrink-0"
            >
              <X size={15} />
            </button>
          </div>
        )}

        <div className="flex items-end gap-2">
          {/* File Attachment Button (hidden while editing — edits are text-only) */}
          {!editingMessage && (
            <label className="flex-shrink-0 cursor-pointer group">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                disabled={uploadingFiles}
                onChange={async (e) => {
                  const MAX_FILE_SIZE = 20 * 1024 * 1024;
                  const files = Array.from(e.target.files);
                  const validFiles = files.filter((f) => f.size <= MAX_FILE_SIZE);
                  if (validFiles.length < files.length) {
                    showAlert({ type: "warning", message: "Some files exceed 20MB and were not added!" });
                  }
                  setSelectedFiles((prev) => [...prev, ...validFiles]);
                  e.target.value = "";
                }}
              />
              <div
                className={`w-11 h-11 flex items-center justify-center rounded-2xl border
                  transition-all duration-200 ${
                    uploadingFiles
                      ? "bg-surface_alt border-border text-text_muted cursor-not-allowed"
                      : "bg-surface_alt border-border text-text_secondary group-hover:bg-primary group-hover:text-white group-hover:border-primary"
                  }`}
              >
                <Paperclip size={18} />
              </div>
            </label>
          )}

          {/* Input pill: textarea + emoji + send are real flex siblings (always aligned) */}
          <div className="flex-1 flex items-end gap-1 min-h-[44px] bg-surface_alt border border-border
            rounded-[22px] pl-4 pr-1.5 py-[5px] transition-colors duration-200
            focus-within:border-primary focus-within:bg-surface">
            <textarea
              ref={textareaRef}
              value={messageInput}
              onChange={(e) => {
                setMessageInput(e.target.value);
                handleTypingEmit();
                e.target.style.height = "auto";
                e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  if (!isSendDisabled) handleSendMessage();
                } else if (e.key === "Escape" && (editingMessage || forwardingMessage)) {
                  e.preventDefault();
                  if (editingMessage) setMessageInput("");
                  setEditingMessage(null);
                  setForwardingMessage(null);
                  e.target.style.height = "auto";
                }
              }}
              onPaste={handlePaste}
              disabled={uploadingFiles}
              placeholder={
                uploadingFiles
                  ? "Uploading files..."
                  : editingMessage
                  ? "Edit your message..."
                  : forwardingMessage
                  ? "Add a message (optional)..."
                  : "Type a message..."
              }
              rows={1}
              className="flex-1 min-w-0 bg-transparent border-0 outline-none resize-none
                text-[12.5px] leading-[20px] text-text_primary placeholder:text-text_muted
                py-2 max-h-32 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ overflowY: messageInput.split("\n").length > 3 ? "auto" : "hidden" }}
            />

            {/* Emoji Button + Picker */}
            <div className="relative flex-shrink-0" ref={emojiWrapRef}>
              {showEmojiPicker && (
                <div className="absolute bottom-full right-0 mb-2 z-40">
                  <EmojiPicker onSelect={insertEmoji} />
                </div>
              )}
              <button
                type="button"
                onClick={() => setShowEmojiPicker((s) => !s)}
                className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors
                  ${showEmojiPicker ? "text-primary bg-primary_tint" : "text-text_muted hover:text-primary"}`}
              >
                <Smile size={18} />
              </button>
            </div>

            {/* Send Button */}
            <button
              type="button"
              onClick={handleSendMessage}
              disabled={isSendDisabled}
              className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center
                transition-all duration-200 ${
                  isSendDisabled
                    ? "bg-surface text-text_muted cursor-not-allowed"
                    : "bg-primary text-white hover:bg-primary_hover shadow-sm"
                }`}
            >
              {uploadingFiles ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full
                  animate-spin" />
              ) : (
                <Send size={16} />
              )}
            </button>
          </div>
        </div>

        {/* Helper Text */}
        <p className="text-[10.5px] text-text_muted mt-2 flex items-center gap-2 pl-1">
          <span>Enter to send</span>
          <span className="w-1 h-1 bg-border rounded-full" />
          <span>Shift + Enter for new line</span>
          {(editingMessage || forwardingMessage) && (
            <>
              <span className="w-1 h-1 bg-border rounded-full" />
              <span>Esc to cancel</span>
            </>
          )}
        </p>
      </div>
      )}

      {/* Delete Confirmation */}
      {confirmData && (
        <ConfirmDialogue confirmData={confirmData} handleConfirmClose={handleConfirmClose} />
      )}

      {/* Message Info */}
      {infoMessage && (
        <MessageInfoModal
          message={infoMessage}
          isOwn={infoMessage.sender_fid === currentLoggedInUserId}
          onClose={() => setInfoMessage(null)}
        />
      )}
    </div>
  );
}